/**
 * "Is what's on screen still the report I loaded?"
 *
 * Answered by comparing fingerprints rather than by watching for edits.
 */

import type {
    SummaryReportChartState,
    SummaryReportGridState,
    SummaryReportPivotState,
} from '../types.ts';

/**
 * The parts of the grid's exported state a saved report is made of.
 *
 * A list rather than a set of exclusions: the blob gains keys as MUI grows, and a new one
 * should have to be considered rather than silently start marking reports unsaved.
 */
const TRACKED_GRID_SLICES = [
    'columns',
    'sorting',
    'filter',
    'aggregation',
    'rowGrouping',
    'pinnedColumns',
    'density',
] as const;

/** Everything a fingerprint covers — the report, or the view claiming to be it. */
export interface SummaryReportSnapshot {
    gridState?: SummaryReportGridState;
    pivotState?: SummaryReportPivotState;
    chartState?: SummaryReportChartState;
    /** The where-clause behind the data, as much a part of a report as the layout. */
    definitionExpression?: string;
}

/**
 * JSON with object keys in a fixed order.
 *
 * `JSON.stringify` follows insertion order, and the grid has no reason to build a model the
 * same way twice — one rebuilt in a different order is the same model and must not read as
 * an edit. Arrays keep their order, which is meaningful.
 */
function stableStringify(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

    const entries = Object.entries(value as Record<string, unknown>)
        // An absent key and one set to undefined are the same state, and only one of them
        // survives a round trip through storage.
        .filter(([, item]) => item !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`);

    return `{${entries.join(',')}}`;
}

/** The tracked slices of a grid state blob, and nothing else. */
function trackedGridSlices(gridState: SummaryReportGridState | undefined) {
    const source = (gridState ?? {}) as Record<string, unknown>;
    const tracked: Record<string, unknown> = {};

    TRACKED_GRID_SLICES.forEach((slice) => {
        if (source[slice] !== undefined) tracked[slice] = source[slice];
    });

    return tracked;
}

/**
 * A string that changes when the report would save differently, and not otherwise.
 *
 * Only ever compared with another fingerprint. Nothing should be read back out of it.
 */
export function summaryReportFingerprint(snapshot: SummaryReportSnapshot): string {
    return stableStringify({
        grid: trackedGridSlices(snapshot.gridState),
        pivot: snapshot.pivotState,
        // The chart's fields, its type and the name the user gave it.
        chart: snapshot.chartState,
        filter: snapshot.definitionExpression ?? '',
    });
}
